import { useState, useEffect, useCallback, useRef } from 'react';
import googleAuthService from '../services/googleAuth';
import googleDriveService from '../services/googleDrive';
import googlePhotosService from '../services/googlePhotos';
import googleTasksService from '../services/googleTasks';
import googleCalendarService from '../services/googleCalendar';

// Hook centralisé pour tous les services Google
export function useGoogleServices() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [servicesStatus, setServicesStatus] = useState({
    drive: false,
    photos: false,
    tasks: false,
    calendar: false,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  /**
   * État du token OAuth Google :
   *   'none'      → non connecté
   *   'connected' → token valide
   *   'expiring'  → expire dans moins de 5 min
   *   'expired'   → expiré, reconnexion requise
   */
  const [tokenStatus, setTokenStatus] = useState('none');
  const tokenPollRef = useRef(null);

  // Initialiser les services Google
  useEffect(() => {
    const initGoogle = async () => {
      try {
        setIsInitializing(true);
        setError(null);

        await googleAuthService.init((signedIn, userData) => {
          setIsSignedIn(signedIn);
          setUser(userData);

          if (signedIn) {
            // Initialiser les autres services en arrière-plan
            initOtherServices();
          }
        });

        // Vérifier si déjà connecté
        if (googleAuthService.checkSignedIn()) {
          setIsSignedIn(true);
          setUser(googleAuthService.getUser());
          await initOtherServices();
        }
      } catch (err) {
        console.error('Erreur initialisation Google:', err);
        setError('Erreur de connexion à Google. Vérifiez votre connexion internet.');
      } finally {
        setIsInitializing(false);
      }
    };

    initGoogle();
  }, []);

  // Polling du statut du token toutes les 30 secondes
  useEffect(() => {
    const checkStatus = () => {
      setTokenStatus(googleAuthService.getTokenStatus());
    };

    // Enregistrer le callback d'expiration
    googleAuthService.setOnTokenExpiry(() => {
      setTokenStatus('expired');
    });

    checkStatus(); // Vérification immédiate
    tokenPollRef.current = setInterval(checkStatus, 30 * 1000);

    return () => {
      clearInterval(tokenPollRef.current);
    };
  }, []);

  // Mettre à jour tokenStatus quand l'état de connexion change
  useEffect(() => {
    if (!isSignedIn) {
      setTokenStatus('none');
    } else {
      setTokenStatus(googleAuthService.getTokenStatus());
    }
  }, [isSignedIn]);

  // Initialiser les services secondaires
  const initOtherServices = async () => {
    const status = { drive: false, photos: false, tasks: false, calendar: false };

    // Wait a bit for GAPI to finish loading if needed
    if (!window.gapi?.client) {
      console.log('⏳ En attente du chargement GAPI...');
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    // S'assurer que le token est configuré dans GAPI
    const accessToken = googleAuthService.getAccessToken();
    if (accessToken && window.gapi?.client?.setToken) {
      const currentToken = window.gapi.client.getToken?.();
      if (!currentToken?.access_token) {
        window.gapi.client.setToken({ access_token: accessToken });
        console.log('🔑 Token configuré dans GAPI depuis useGoogleServices');
      }
    } else if (!accessToken) {
      // Token not available yet — services will work once user triggers a Google action
      console.log('ℹ️ Token OAuth non disponible — les APIs Google seront activees au prochain login');
      // Don't return — still try to detect available APIs
    }

    // Check which APIs are available
    const calendarAvailable = googleAuthService.isApiAvailable('calendar');
    const driveAvailable = googleAuthService.isApiAvailable('drive');
    const tasksAvailable = googleAuthService.isApiAvailable('tasks');

    // Google Calendar - Toujours essayer de l'activer si connecté
    if (calendarAvailable || window.gapi?.client?.calendar) {
      status.calendar = true;
      console.log('✅ Google Calendar disponible');
      // Démarrer l'import automatique des événements
      googleCalendarService.autoImportEnabled = true;
      googleCalendarService.startEventImport();
    } else if (window.gapi?.client) {
      // Tenter de charger l'API Calendar
      try {
        await window.gapi.client.load('calendar', 'v3');
        status.calendar = true;
        console.log('✅ Google Calendar chargé dynamiquement');
        googleCalendarService.autoImportEnabled = true;
        googleCalendarService.startEventImport();
      } catch (e) {
        console.warn('⚠️ Google Calendar API non disponible:', e.message);
      }
    }

    // Google Drive
    if (driveAvailable && window.gapi?.client?.drive?.files) {
      try {
        await googleDriveService.initAppFolder();
        status.drive = true;
        console.log('✅ Google Drive initialisé');
      } catch (e) {
        console.warn('⚠️ Drive erreur init:', e.message);
      }
    } else if (window.gapi?.client) {
      // Tenter de charger l'API Drive
      try {
        await window.gapi.client.load('drive', 'v3');
        await googleDriveService.initAppFolder();
        status.drive = true;
        console.log('✅ Google Drive chargé dynamiquement');
      } catch (e) {
        console.warn('⚠️ Google Drive non disponible:', e.message);
      }
    }

    // Google Tasks - Activer si disponible
    if (tasksAvailable || window.gapi?.client?.tasks) {
      status.tasks = true;
      console.log('✅ Google Tasks disponible');
    } else if (window.gapi?.client) {
      // Tenter de charger l'API Tasks
      try {
        await window.gapi.client.load('tasks', 'v1');
        await googleTasksService.initTaskList();
        if (googleTasksService.taskListId) {
          status.tasks = true;
          console.log('✅ Google Tasks chargé dynamiquement');
        }
      } catch (e) {
        // 403 = API non activée, c'est optionnel
        if (e.status !== 403) {
          console.warn('⚠️ Google Tasks non disponible:', e.message);
        }
      }
    }

    // Google Photos désactivé - les preuves sont stockées sur Google Drive
    status.photos = false;

    setServicesStatus(status);
    console.log('📊 Services Google:', status);
  };

  // Se connecter
  const signIn = useCallback(async () => {
    console.log('🚀 useGoogleServices.signIn() appelé');
    try {
      setError(null);
      const userData = await googleAuthService.signIn();
      console.log('✅ signIn réussi, userData:', userData);
      if (userData) {
        setIsSignedIn(true);
        setUser(userData);
        await initOtherServices();
      }
      return userData;
    } catch (err) {
      console.error('❌ Erreur connexion dans useGoogleServices:', err);
      setError('Erreur lors de la connexion. Veuillez réessayer.');
      throw err;
    }
  }, []);

  // Reconnecter silencieusement (après expiration de token)
  // Déclenche un nouveau signIn sans vider les données locales
  const reconnect = useCallback(async () => {
    try {
      setError(null);
      const userData = await googleAuthService.signIn();
      if (userData) {
        setIsSignedIn(true);
        setUser(userData);
        setTokenStatus('connected');
        await initOtherServices();
      }
      return userData;
    } catch (err) {
      console.error('Erreur reconnexion:', err);
      setError('Reconnexion échouée. Veuillez réessayer.');
      return null;
    }
  }, []);

  // Se déconnecter
  const signOut = useCallback(() => {
    googleAuthService.signOut();
    setIsSignedIn(false);
    setUser(null);
    setServicesStatus({
      drive: false,
      photos: false,
      tasks: false,
      calendar: false,
    });
  }, []);

  // Synchroniser les tâches avec Drive
  const syncTasks = useCallback(async (tasks) => {
    if (!isSignedIn || !servicesStatus.drive) {
      console.warn('Synchronisation impossible: non connecté ou Drive non disponible');
      return false;
    }

    try {
      setIsSyncing(true);
      await googleDriveService.saveTasks(tasks);
      setLastSync(new Date());
      return true;
    } catch (err) {
      console.error('Erreur synchronisation:', err);
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [isSignedIn, servicesStatus.drive]);

  // Charger les tâches depuis Drive
  const loadTasksFromDrive = useCallback(async () => {
    if (!isSignedIn || !servicesStatus.drive) {
      return null;
    }

    try {
      return await googleDriveService.loadTasks();
    } catch (err) {
      console.error('Erreur chargement tâches:', err);
      return null;
    }
  }, [isSignedIn, servicesStatus.drive]);

  // Uploader une preuve photo
  // taskInfo: { category, task, date } pour l'organisation des dossiers
  const uploadProofPhoto = useCallback(async (imageData, taskId, taskInfo = {}) => {
    const results = { drive: null, photos: null };

    console.log('📤 uploadProofPhoto appelé:', {
      isSignedIn,
      driveStatus: servicesStatus.drive,
      photosStatus: servicesStatus.photos,
      taskInfo
    });

    if (!isSignedIn) {
      console.warn('⚠️ Upload annulé: non connecté');
      return results;
    }

    // Upload sur Drive (fiable) avec organisation par dossiers
    if (servicesStatus.drive) {
      try {
        console.log('📤 Début upload Drive...');
        results.drive = await googleDriveService.uploadProofImage(
          imageData,
          taskId,
          `proof-${Date.now()}`,
          taskInfo // { category, task, date }
        );
        console.log('✅ Upload Drive réussi:', results.drive);
      } catch (e) {
        console.error('❌ Erreur upload Drive:', e);
        throw e; // Remonter l'erreur pour le toast
      }
    } else {
      console.warn('⚠️ Drive non disponible, status:', servicesStatus);
      // Tenter quand même si GAPI est prêt
      if (googleDriveService.isReady()) {
        console.log('📤 Tentative upload Drive malgré status...');
        try {
          results.drive = await googleDriveService.uploadProofImage(
            imageData,
            taskId,
            `proof-${Date.now()}`,
            taskInfo
          );
          console.log('✅ Upload Drive réussi (fallback):', results.drive);
        } catch (e) {
          console.error('❌ Erreur upload Drive (fallback):', e);
        }
      }
    }

    // Upload sur Photos (optionnel)
    if (servicesStatus.photos) {
      try {
        results.photos = await googlePhotosService.uploadImage(
          imageData,
          `ADHD Planner - Preuve pour ${taskInfo.task || taskId}`
        );
      } catch (e) {
        console.warn('Erreur upload Photos:', e);
      }
    }

    return results;
  }, [isSignedIn, servicesStatus.drive, servicesStatus.photos]);

  // Ajouter une note
  const addNote = useCallback(async (title, content, dueDate = null) => {
    if (!isSignedIn || !servicesStatus.tasks) {
      return null;
    }

    try {
      return await googleTasksService.addNote(title, content, dueDate);
    } catch (err) {
      console.error('Erreur ajout note:', err);
      return null;
    }
  }, [isSignedIn, servicesStatus.tasks]);

  // Exporter en CSV
  const exportToCSV = useCallback(async (tasks, completedTasks, proofs) => {
    if (!isSignedIn || !servicesStatus.drive) {
      return null;
    }

    try {
      return await googleDriveService.exportToCSV(tasks, completedTasks, proofs);
    } catch (err) {
      console.error('Erreur export CSV:', err);
      return null;
    }
  }, [isSignedIn, servicesStatus.drive]);

  // Synchroniser les tâches locales vers Google Tasks
  const syncLocalTasksToGoogle = useCallback(async (localTasks) => {
    if (!isSignedIn || !servicesStatus.tasks) {
      return { synced: 0, errors: [], message: 'Google Tasks non disponible' };
    }

    try {
      setIsSyncing(true);
      const result = await googleTasksService.syncTasksToGoogle(localTasks);
      setLastSync(new Date());
      return { ...result, message: `${result.synced} tâche(s) synchronisée(s)` };
    } catch (err) {
      console.error('Erreur sync Google Tasks:', err);
      return { synced: 0, errors: [err.message], message: 'Erreur de synchronisation' };
    } finally {
      setIsSyncing(false);
    }
  }, [isSignedIn, servicesStatus.tasks]);

  // Récupérer les tâches depuis Google Tasks
  const getGoogleTasks = useCallback(async (showCompleted = false) => {
    if (!isSignedIn || !servicesStatus.tasks) {
      return [];
    }

    try {
      return await googleTasksService.getNotes(showCompleted);
    } catch (err) {
      console.error('Erreur récupération Google Tasks:', err);
      return [];
    }
  }, [isSignedIn, servicesStatus.tasks]);

  // Marquer une tâche Google comme complétée
  const completeGoogleTask = useCallback(async (taskId) => {
    if (!isSignedIn || !servicesStatus.tasks) {
      return null;
    }

    try {
      return await googleTasksService.completeNote(taskId);
    } catch (err) {
      console.error('Erreur completion Google Task:', err);
      return null;
    }
  }, [isSignedIn, servicesStatus.tasks]);

  // PUSH: Envoyer les tâches vers Google Calendar
  const pushToCalendar = useCallback(async (tasks, date) => {
    if (!isSignedIn) {
      return { pushed: 0, errors: [], message: 'Non connecté' };
    }

    try {
      setIsSyncing(true);
      const result = await googleCalendarService.pushTasksToCalendar(tasks, date);
      setLastSync(new Date());
      return result;
    } catch (err) {
      console.error('Erreur push Calendar:', err);
      return { pushed: 0, errors: [err.message], message: 'Erreur de synchronisation' };
    } finally {
      setIsSyncing(false);
    }
  }, [isSignedIn]);

  // PULL: Importer les événements depuis Google Calendar
  const pullFromCalendar = useCallback(async (date) => {
    if (!isSignedIn) {
      return { pulled: 0, events: [], message: 'Non connecté' };
    }

    try {
      setIsSyncing(true);
      const result = await googleCalendarService.pullEventsFromCalendar(date);
      setLastSync(new Date());
      return result;
    } catch (err) {
      console.error('Erreur pull Calendar:', err);
      return { pulled: 0, events: [], message: 'Erreur: ' + err.message };
    } finally {
      setIsSyncing(false);
    }
  }, [isSignedIn]);

  // Obtenir les événements importés pour une date
  const getImportedEvents = useCallback((dateString) => {
    return googleCalendarService.getImportedEventsForDate(dateString);
  }, []);

  return {
    // État
    isInitializing,
    isSignedIn,
    user,
    error,
    servicesStatus,
    isSyncing,
    lastSync,

    // Actions auth
    signIn,
    signOut,
    reconnect,
    tokenStatus,

    // Actions sync
    syncTasks,
    loadTasksFromDrive,
    uploadProofPhoto,
    addNote,
    exportToCSV,

    // Google Tasks
    syncLocalTasksToGoogle,
    getGoogleTasks,
    completeGoogleTask,

    // Google Calendar (Push/Pull)
    pushToCalendar,
    pullFromCalendar,
    getImportedEvents,

    // Services directs (pour usage avancé)
    services: {
      auth: googleAuthService,
      drive: googleDriveService,
      photos: googlePhotosService,
      tasks: googleTasksService,
      calendar: googleCalendarService,
    },
  };
}

export default useGoogleServices;
